import React from "react";
import {
  ApiResponseType,
  BulkDuplicateLookupResponseType,
  PropsType,
} from "./types";
import { usePremisesNavigation } from "../hooks";
import { AppContext } from "src/context/AppContext";
import { getFormBehavior } from "src/components/Form/CloseTransfer/helper";
import { RequestChannelEnum, RequestStatusEnum } from "../types";
import { useAccountAlreadyClosedDialogContext } from "../AccountAlreadyClosedDialog";
import { useDuplicateRequestDialogContext } from "../DuplicateRequestDialog";
import { useDeleteDraftDialogContext } from "src/components/Drafts";
import { useDashboardContext } from "src/templates";
import {
  bulkDuplicateLookup,
  mockPostCloseTransferCheckStatusMarkComplete,
  mockPostCloseTransferCheckStatusUnresolved,
  postCloseTransferCheckStatus,
} from "./services";
import { useBulkDuplicateDialogContext } from "../BulkDuplicateDialog";

export const usePremisesContextMenu = ({
  row,
  homeUser,
  onDeleteDraftConfirmed,
  reFetchPremises,
}: PropsType) => {
  const {
    handleOnClick: handleOnClickView,
    navigate,
    location,
  } = usePremisesNavigation(row);
  const {
    context: { user },
  } = React.useContext(AppContext);

  const { showCTEntrance } = getFormBehavior(
    row.requestStatus,
    row.connectionType,
    row.requestChannel
  );

  const showDeleteDraftEntrance =
    row.requestStatus === RequestStatusEnum.DRAFT ||
    row.requestStatus === RequestStatusEnum.DRAFT_ASSIGNED;
  const showDuplicateRequestEntrance =
    row.requestChannel === RequestChannelEnum.ORIGIN_CONNECT && !homeUser;
  const { openDialog: openBulkDuplicateDialog } =
    useBulkDuplicateDialogContext();
  const { openDialog: openAccountAlreadyClosedDialog } =
    useAccountAlreadyClosedDialogContext();
  const { openDialog: openDuplicateRequestDialog } =
    useDuplicateRequestDialogContext();
  const { openDialog: openDeleteDraftDialog } = useDeleteDraftDialogContext();

  const { snackbarSection } = useDashboardContext();

  const { hash } = location;

  const handleOnClickDuplicate = React.useCallback(() => {
    openDuplicateRequestDialog({
      onDuplicateRequest: () => {
        navigate(`/new-premise`, {
          state: {
            isDuplicate: true,
            duplicateSourceRequestId: row.id,
          },
        });
      },
    });
  }, [openDuplicateRequestDialog, navigate, row.id]);

  const handleOnClickBulkDuplicate = React.useCallback(async () => {
    if (row.premiseId) {
      return await bulkDuplicateLookup(row.premiseId);
    } else {
      throw new Error("No premiseId found");
    }
  }, [navigate, row.premiseId]);

  const handleOnResultBulkDuplicate = React.useCallback(
    (theApiResponse: BulkDuplicateLookupResponseType) => {
      openBulkDuplicateDialog({ ...theApiResponse, reFetchPremises });
    },
    [openBulkDuplicateDialog, user, navigate, row.id]
  );

  const handleOnErrorBulkDuplicate = React.useCallback(
    (error: unknown) => {
      console.log(error);
      snackbarSection.show({
        children: "Failed to open Bulk Duplicate",
        showClose: true,
        autoHideDuration: 5000,
        tone: "critical",
      });
    },
    [snackbarSection]
  );

  const handleOnClickDelete = React.useCallback(() => {
    openDeleteDraftDialog({
      onDeleteDraft: onDeleteDraftConfirmed,
    });
  }, [openDeleteDraftDialog, onDeleteDraftConfirmed]);

  const handleOnClickClose = React.useCallback(async () => {
    // TODO https://origindd.atlassian.net/browse/LOC-1179
    if (row.journeyId) {
      switch (hash) {
        /* istanbul ignore next */
        case "#mark-complete": {
          return await mockPostCloseTransferCheckStatusMarkComplete(
            row.journeyId
          );
        }
        /* istanbul ignore next */
        case "#unresolved": {
          return await mockPostCloseTransferCheckStatusUnresolved(
            row.journeyId
          );
        }
        default: {
          return await postCloseTransferCheckStatus(row.journeyId);
        }
      }
    } else {
      throw new Error("No journeyId found");
    }
  }, [row.journeyId, hash]);

  const handleOnResultClose = React.useCallback(
    (theApiResponse: ApiResponseType) => {
      switch (theApiResponse.checkOutcome) {
        case "MARK_COMPLETE": {
          openAccountAlreadyClosedDialog({
            requestingCompany: theApiResponse.requestingCompany,
          });
          break;
        }
        default: {
          // UNRESOLVED
          navigate(`/new-premise/${row.id}/close-transfer`);
          break;
        }
      }
    },
    [openAccountAlreadyClosedDialog, user, navigate, row.id]
  );

  const handleOnErrorClose = React.useCallback(
    (error: unknown) => {
      console.log(error);
      snackbarSection.show({
        children: "Failed to fetch status",
        showClose: true,
        autoHideDuration: 5000,
        tone: "critical",
      });
    },
    [snackbarSection]
  );

  return {
    handleOnClickView,
    handleOnClickDuplicate,
    handleOnClickBulkDuplicate,
    handleOnResultBulkDuplicate,
    handleOnErrorBulkDuplicate,
    handleOnClickDelete,
    handleOnClickClose,
    handleOnResultClose,
    handleOnErrorClose,
    showCTEntrance,
    showDeleteDraftEntrance,
    showDuplicateRequestEntrance,
  };
};
